import React, { useCallback, useEffect, useRef, useState } from "react";
import { Field } from "redux-form";
import { useSelector } from "react-redux";
import moment from "moment";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import { ContentState, EditorState, convertToRaw } from "draft-js";

import emailEditor from "../../../../common/EmailEditor";

import styles from "../../JobStyle.module.css";
import "./JobSwmsFormStyle.css";

/* ── Main section component ── */
const AuthorisationDetailsSection = ({ approvers = [], loading, change, notesFieldName = "authorisation_details.notes" }) => {
  const getNestedValue = (obj, path) => path.split(".").reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
  const notesValue = useSelector((state) => {
    const formState = state && state.form && state.form.job_swms_form ? state.form.job_swms_form : {};
    return getNestedValue(formState.values, notesFieldName) || getNestedValue(formState.initial, notesFieldName) || "";
  });

  const [editorState, setEditorState] = useState(() => EditorState.createEmpty());
  const lastEditorHtmlRef = useRef("");

  const getEditorStateFromHtml = useCallback((html = "") => {
    const blocksFromHtml = htmlToDraft(html);
    if (blocksFromHtml && blocksFromHtml.contentBlocks) {
      const contentState = ContentState.createFromBlockArray(blocksFromHtml.contentBlocks);
      return EditorState.createWithContent(contentState);
    }

    return EditorState.createEmpty();
  }, []);

  useEffect(() => {
    if ((notesValue || "") === lastEditorHtmlRef.current) {
      return;
    }

    setEditorState(getEditorStateFromHtml(notesValue || ""));
  }, [getEditorStateFromHtml, notesValue]);

  const onEditorStateChange = useCallback((newState) => {
    setEditorState(newState);
    if (change) {
      const html = draftToHtml(convertToRaw(newState.getCurrentContent()));
      lastEditorHtmlRef.current = html;
      change(notesFieldName, html);
    }
  }, [change, notesFieldName]);

  const creatorRows = (approvers || []).filter((a) => a.action === "Created");
  const otherRows = (approvers || []).filter((a) => a.action !== "Created");
  const totalRows = creatorRows.length + otherRows.length;

  return (
    <div className="my-2">
      <div className="container-fluid px-0">
        {/* Authorisation Notes — rich text */}
        <div className="row mb-2">
          <div className="col-12">
            <div className={styles.Label}>Authorisation Notes</div>
            <Field
              name={notesFieldName}
              component={emailEditor}
              editorState={editorState}
              onEditorStateChange={onEditorStateChange}
              disabled={loading}
            />
          </div>
        </div>

        {/* Approvers Table */}
        <div className="row">
          <div className="col-12">
            <table
              className="table-sm cs-table cs-table-v2 cs-table-job-swms"
              style={{ tableLayout: "fixed", width: "100%" }}
            >
              <thead>
                <tr>
                  <th style={{ width: "40px" }}></th>
                  <th style={{ width: "20%" }}>Name</th>
                  <th style={{ width: "18%" }}>Position</th>
                  <th style={{ width: "12%" }}>Action</th>
                  <th style={{ width: "12%" }}>Date</th>
                  <th style={{ width: "34%" }}>Signature</th>
                  {/* <th style={{ width: "12%", textAlign: "center" }}>In Document</th> */}
                </tr>
              </thead>
              <tbody>
                {totalRows > 0 ? (
                  <>
                    {/* Creator row — always first, not draggable */}
                    {creatorRows.map((approver, i) => (
                      <tr key={`creator-${i}`}>
                        <td style={{ textAlign: "center" }}>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 640 640"
                            style={{ height: "16px", width: "16px", color: "#cbd5e1", fill: "currentColor", verticalAlign: "middle" }}
                            title="Creator"
                          >
                            <path d="M256 160L256 224L384 224L384 160C384 124.7 355.3 96 320 96C284.7 96 256 124.7 256 160zM192 224L192 160C192 89.3 249.3 32 320 32C390.7 32 448 89.3 448 160L448 224C483.3 224 512 252.7 512 288L512 512C512 547.3 483.3 576 448 576L192 576C156.7 576 128 547.3 128 512L128 288C128 252.7 156.7 224 192 224z" />
                          </svg>
                        </td>
                        <td style={{ fontWeight: "600" }}>{approver.name || ""}</td>
                        <td>{approver.position || ""}</td>
                        <td>
                          <span className="swms-auth-status-badge swms-auth-badge-prepared">Prepared</span>
                        </td>
                        <td>
                          {approver.date ? moment.utc(approver.date).format("DD/MM/YYYY") : ""}
                        </td>
                        <td>
                          {approver.signature ? (
                            <img
                              src={approver.signature}
                              alt="signature"
                              style={{ height: "30px", maxWidth: "100%", objectFit: "contain" }}
                            />
                          ) : (
                            <span>—</span>
                          )}
                        </td>
                      </tr>
                    ))}

                    {/* Other rows (pending, approved, etc.) — at bottom */}
                    {otherRows.map((approver, i) => (
                      <tr key={`other-${i}`}>
                        <td style={{ textAlign: "center" }}>
                          {approver.action === "Pending" ? (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 640 640"
                              style={{ height: "16px", width: "16px", color: "#93c5fd", fill: "currentColor", verticalAlign: "middle" }}
                              title="Pending approval"
                            >
                              <path d="M320 64C461.4 64 576 178.6 576 320C576 461.4 461.4 576 320 576C178.6 576 64 461.4 64 320C64 178.6 178.6 64 320 64zM296 184L296 320C296 328 300 335.5 306.7 340L402.7 404C413.7 411.4 428.6 408.4 436 397.3C443.4 386.2 440.4 371.4 429.3 364L344 307.2L344 184C344 170.7 333.3 160 320 160C306.7 160 296 170.7 296 184z" />
                            </svg>
                          ) : approver.action === "Rejected" ? (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 640 640"
                              style={{ height: "16px", width: "16px", color: "#ef4444", fill: "currentColor", verticalAlign: "middle" }}
                              title="Rejected"
                            >
                              <path d="M320 576C461.4 576 576 461.4 576 320C576 178.6 461.4 64 320 64C178.6 64 64 178.6 64 320C64 461.4 178.6 576 320 576zM231 231C240.4 221.6 255.6 221.6 264.9 231L319.9 286L374.9 231C384.3 221.6 399.5 221.6 408.8 231C418.1 240.4 418.2 255.6 408.8 264.9L353.8 319.9L408.8 374.9C418.2 384.3 418.2 399.5 408.8 408.8C399.4 418.1 384.2 418.2 374.9 408.8L319.9 353.8L264.9 408.8C255.5 418.2 240.3 418.2 231 408.8C221.7 399.4 221.6 384.2 231 374.9L286 319.9L231 264.9C221.6 255.5 221.6 240.3 231 231z" />
                            </svg>
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 640 640"
                              style={{ height: "16px", width: "16px", color: "#10b981", fill: "currentColor", verticalAlign: "middle" }}
                              title="Approved"
                            >
                              <path d="M320 576C178.6 576 64 461.4 64 320C64 178.6 178.6 64 320 64C461.4 64 576 178.6 576 320C576 461.4 461.4 576 320 576zM438 209.7C427.3 201.9 412.3 204.3 404.5 215L285.1 379.2L233 327.1C223.6 317.7 208.4 317.7 199.1 327.1C189.8 336.5 189.7 351.7 199.1 361L271.1 433C276.1 438 282.9 440.5 289.9 440C296.9 439.5 303.3 435.9 307.4 430.2L443.3 243.2C451.1 232.5 448.7 217.5 438 209.7z" />
                            </svg>
                          )}
                        </td>
                        <td style={{ fontWeight: "600" }}>{approver.name || ""}</td>
                        <td>{approver.position || ""}</td>
                        <td>
                          <span className={`swms-auth-status-badge ${
                            approver.action === "Pending" ? "swms-auth-badge-pending" : approver.action === "Rejected" ? "swms-auth-badge-rejected" : "swms-auth-badge-approved"
                          }`}>
                            {approver.action || "Pending"}
                          </span>
                        </td>
                        <td>
                          {approver.date ? moment.utc(approver.date).format("DD/MM/YYYY") : ""}
                        </td>
                        <td>
                          {approver.signature ? (
                            <img
                              src={approver.signature}
                              alt="signature"
                              style={{ height: "30px", maxWidth: "100%", objectFit: "contain" }}
                            />
                          ) : (
                            <span>—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </>
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center" style={{ color: "#94a3b8" }}>
                      No approvers available.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthorisationDetailsSection;
